import React, { FC, memo, useState, useEffect, useCallback, useMemo } from 'react';
import { ProductSetRelatedProductViewModel, ProductSize } from '~/lib/data-contract';
import { Typography } from '~/shared/components/typography/typography.component';
import { Button } from '~/shared/components/button/button.component';
import { PriceGroup } from '~/shared/components/price-group/price-group.component';
import ProductImage from '~/shared/components/product-image/product-image.component';
import styles from './product-set-item.module.css';
import ChevronDown from '$icons/chevron-down.svg';
import { useTranslation } from '~/shared/utils';
import { Modal } from '~/shared/components/modal/modal.component';
import { SizeSelector } from '~/shared/components/size-selector/size-selector.component';
import clsx from 'clsx';
import Easysize from '../../easy-size/easy-size.component';
import { CMSLink } from '~/shared/components';

type Props = {
    item: ProductSetRelatedProductViewModel;
    onSelectVariant: (size: ProductSize, productColorId: string) => void;
    selectedSize?: ProductSize;
    sizeSelectWarning?: boolean;
};

const ProductSetItem: FC<Props> = memo(
    ({ item, onSelectVariant, selectedSize, sizeSelectWarning }) => {
        const {
            name,
            subtitle,
            imageUrl,
            priceDetails,
            sizes,
            productColorId = '',
            sizingText,
            url,
        } = item;

        // Memoize warning calculation
        const warning = useMemo(() => sizeSelectWarning && !selectedSize, [
            sizeSelectWarning,
            selectedSize,
        ]);

        const [isOpen, setIsOpen] = useState(false);

        const { translate } = useTranslation();

        const handleModalClose = useCallback(() => setIsOpen(false), []);
        const handleModalOpen = useCallback(() => setIsOpen(true), []);

        const handleSizeSelect = useCallback(
            (val: string) => {
                const selectedSize = sizes?.find((size) => size.variantId === val);
                if (selectedSize) {
                    onSelectVariant(selectedSize, productColorId);
                }
            },
            [sizes, onSelectVariant, productColorId]
        );

        // Memoize input name
        const selectorInputName = useMemo(() => `${productColorId}-pdp-set-size-selector`, [
            productColorId,
        ]);

        const buttonText = useMemo(() => {
            return selectedSize
                ? `${translate('productDetails.selectedSize')} ${selectedSize.size}`
                : translate('productDetails.selectSize');
        }, [selectedSize, translate]);

        // Memoize modal footer to prevent unnecessary re-renders
        const modalFooter = useMemo(() => {
            return selectedSize ? (
                <Button onClick={handleModalClose} fullWidth centeredText>
                    {translate('productDetails.approveSizeButton')}
                </Button>
            ) : null;
        }, [selectedSize, handleModalClose, translate]);

        // Memoize button className to prevent clsx recalculation
        const buttonClassName = useMemo(() => {
            return clsx(styles.button, {
                [styles.warning]: warning,
            });
        }, [warning]);

        // Auto-select size if only one size is available and no size is currently selected
        useEffect(() => {
            if (!selectedSize && sizes && sizes.length === 1) {
                const singleSize = sizes[0];
                onSelectVariant(singleSize, productColorId);
            }
        }, [selectedSize, sizes, onSelectVariant, productColorId]);

        return (
            <>
                <div className={styles.root}>
                    <CMSLink href={url} className={styles.imageWrapper}>
                        <ProductImage
                            sizes={`300px`}
                            className={styles.image}
                            image={imageUrl}
                            width={120}
                            height={140}
                        />
                    </CMSLink>
                    <div className={styles.details}>
                        <CMSLink href={url} className={styles.detailsHeader}>
                            {name && (
                                <Typography variant="display5" as="h4">
                                    {name}
                                </Typography>
                            )}
                            {subtitle && <Typography variant="bodyXs">{subtitle}</Typography>}
                        </CMSLink>
                        {priceDetails && <PriceGroup {...priceDetails} variant="display4" />}
                        <Button
                            size="small"
                            variant="secondary"
                            onClick={handleModalOpen}
                            className={buttonClassName}
                        >
                            {buttonText} <ChevronDown />
                        </Button>
                    </div>
                </div>
                <Modal
                    headline={translate('reservation.overlayHeading')}
                    isOpen={isOpen}
                    onClose={handleModalClose}
                    variant="drawerRight"
                    scrollLock={true}
                    darkHeader
                    footer={modalFooter}
                >
                    <div className={styles.modalContent}>
                        <div className={styles.modalHeader}>
                            <div className={styles.imageWrapper}>
                                <ProductImage
                                    sizes={`300px`}
                                    className={styles.image}
                                    image={imageUrl}
                                    width={120}
                                    height={140}
                                />
                            </div>
                            <div>
                                {name && (
                                    <Typography variant="display5" as="h4">
                                        {name}
                                    </Typography>
                                )}
                                {subtitle && <Typography variant="bodyXs">{subtitle}</Typography>}
                            </div>
                        </div>
                        <SizeSelector
                            sizingText={sizingText}
                            sizes={sizes}
                            inputName={selectorInputName}
                            selectedVariantId={selectedSize?.variantId}
                            onSelect={handleSizeSelect}
                        />
                        <Easysize
                            disableAddToBasket
                            product={item}
                            activeSizeId={selectedSize?.variantId}
                            onSizeFound={handleSizeSelect}
                        />
                    </div>
                </Modal>
            </>
        );
    }
);

ProductSetItem.displayName = 'ProductSetItem';
export default ProductSetItem;
